import { clientViewSdk, getApiBaseUrl } from "./sdkClient";

export { getApiBaseUrl, mapSdkError } from "./sdkClient";

export function getManifest(slug, options = {}) {
  return clientViewSdk.getManifest(slug, options);
}

export function getPreviewManifest(slug, previewToken, options = {}) {
  return clientViewSdk.getPreviewManifest(slug, previewToken, options);
}

export function getListings(slug, params = {}, options = {}) {
  return clientViewSdk.getListings(slug, params, options);
}

function publicListingsQuery(params = {}) {
  const qs = new URLSearchParams();
  Object.entries(params || {}).forEach(([key, value]) => {
    if (value == null || value === "") {
      return;
    }
    qs.set(key, String(value));
  });
  return qs.toString() ? `?${qs.toString()}` : "";
}

export function getListingMapPins(slug, params = {}, options = {}) {
  if (typeof clientViewSdk.getListingMapPins === "function") {
    return clientViewSdk.getListingMapPins(slug, params, options);
  }
  return fetch(
    `${getApiBaseUrl()}/api/v1/views/${encodeURIComponent(slug)}/listings/map${publicListingsQuery(params)}`,
    { credentials: "include", ...(options.signal ? { signal: options.signal } : {}) }
  ).then(async (res) => {
    if (!res.ok) {
      throw Object.assign(new Error("Failed to fetch listing map pins"), { status: res.status });
    }
    return res.json();
  });
}

export function getListingAggregates(slug, params = {}, options = {}) {
  if (typeof clientViewSdk.getListingAggregates === "function") {
    return clientViewSdk.getListingAggregates(slug, params, options);
  }
  return fetch(
    `${getApiBaseUrl()}/api/v1/views/${encodeURIComponent(slug)}/listings/aggregates${publicListingsQuery(params)}`,
    { credentials: "include", ...(options.signal ? { signal: options.signal } : {}) }
  ).then(async (res) => {
    if (!res.ok) {
      throw Object.assign(new Error("Failed to fetch listing aggregates"), { status: res.status });
    }
    return res.json();
  });
}

export async function getMarketPriceTrend(slug, params = {}) {
  const qs = new URLSearchParams();
  if (params.days) qs.set("days", String(params.days));
  if (params.municipality) qs.set("municipality", params.municipality);
  const suffix = qs.toString() ? `?${qs.toString()}` : "";
  const res = await fetch(
    `${getApiBaseUrl()}/api/v1/views/${encodeURIComponent(slug)}/market-price-trend${suffix}`,
    { credentials: "include" }
  );
  if (!res.ok) {
    throw Object.assign(new Error("Failed to fetch market price trend"), { status: res.status });
  }
  return res.json();
}

export async function getMarketPulse(slug, options = {}) {
  const res = await fetch(
    `${getApiBaseUrl()}/api/v1/views/${encodeURIComponent(slug)}/market-pulse`,
    { credentials: "include", ...(options.signal ? { signal: options.signal } : {}) }
  );
  if (!res.ok) {
    throw Object.assign(new Error("Failed to fetch market pulse"), { status: res.status });
  }
  return res.json();
}

export function getListingDetail(slug, listingId, params = {}, options = {}) {
  return clientViewSdk.getListingDetail(slug, listingId, params, options);
}

export function submitLead(slug, payload, options = {}) {
  return clientViewSdk.submitLead(slug, payload, options);
}

export async function getLeadStatus(slug, leadId, token, { locale = "es" } = {}) {
  const params = new URLSearchParams();
  if (token) {
    params.set("token", token);
  }
  if (locale) {
    params.set("locale", locale);
  }
  const suffix = params.toString() ? `?${params.toString()}` : "";
  const res = await fetch(
    `${getApiBaseUrl()}/api/v1/views/${encodeURIComponent(slug)}/leads/${encodeURIComponent(leadId)}/status${suffix}`,
    { credentials: "include" }
  );
  if (!res.ok) {
    throw Object.assign(new Error("Failed to fetch lead status"), { status: res.status });
  }
  return res.json();
}

export function getDataset(slug, dataSourceId, params = {}, options = {}) {
  return clientViewSdk.getDataset(slug, dataSourceId, params, options);
}

export function getPwaManifest(slug, options = {}) {
  return clientViewSdk.getPwaManifest(slug, options);
}

export function getSponsorships(slug, params = {}, options = {}) {
  return clientViewSdk.getSponsorships(slug, params, options);
}

export async function trackSponsorshipImpression(slug, placementId, payload) {
  try {
    await clientViewSdk.trackSponsorshipImpression(slug, placementId, payload, {
      baseUrl: getApiBaseUrl(),
    });
  } catch {
    // ignore
  }
}

export async function trackSponsorshipClick(slug, placementId, payload) {
  try {
    await clientViewSdk.trackSponsorshipClick(slug, placementId, payload, {
      baseUrl: getApiBaseUrl(),
    });
  } catch {
    // ignore
  }
}

export function signup(slug, payload) {
  return clientViewSdk.signup(slug, payload);
}

export function login(slug, payload) {
  return clientViewSdk.login(slug, payload);
}

export function logout(slug) {
  return clientViewSdk.logout(slug);
}

export function getCurrentUser(slug) {
  return clientViewSdk.getCurrentUser(slug);
}

export function updateProfile(slug, payload) {
  return clientViewSdk.updateProfile(slug, payload);
}

export function getSavedListings(slug) {
  return clientViewSdk.getSavedListings(slug);
}

export function saveListing(slug, payload, { anonymous = false } = {}) {
  return clientViewSdk.saveListing(slug, payload, { anonymous });
}

export async function submitListingDataReport(slug, listingId, payload) {
  const res = await fetch(
    `${getApiBaseUrl()}/api/public/views/${encodeURIComponent(slug)}/listings/${encodeURIComponent(listingId)}/data-reports`,
    {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload || {}),
    }
  );
  const body = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw Object.assign(new Error(body.message || "Failed to submit listing data report"), {
      status: res.status,
      code: body.code,
      report: body.report || null,
    });
  }
  return body;
}

export function removeSavedListing(slug, savedListingId) {
  return clientViewSdk.removeSavedListing(slug, savedListingId);
}

export function removeSessionSavedListing(slug, savedListingId, visitorId) {
  return clientViewSdk.removeSessionSavedListing(slug, savedListingId, visitorId);
}

export function listSessionSavedListings(slug, visitorId) {
  return clientViewSdk.listSessionSavedListings(slug, visitorId);
}

export function getSavedSearches(slug) {
  return clientViewSdk.getSavedSearches(slug);
}

export function saveSearch(slug, payload, { anonymous = false } = {}) {
  return clientViewSdk.saveSearch(slug, payload, { anonymous });
}

export function updateSavedSearch(slug, savedSearchId, payload) {
  return clientViewSdk.updateSavedSearch(slug, savedSearchId, payload);
}

export function listSavedSearchRevisions(slug, savedSearchId) {
  return clientViewSdk.listSavedSearchRevisions(slug, savedSearchId);
}

export function deleteSavedSearch(slug, savedSearchId) {
  return clientViewSdk.deleteSavedSearch(slug, savedSearchId);
}

export function listSessionSavedSearches(slug, visitorId) {
  return clientViewSdk.listSessionSavedSearches(slug, visitorId);
}

export function mergeGuestActivity(slug, payload) {
  return clientViewSdk.mergeGuestActivity(slug, payload);
}

export function getBuyerProfile(slug) {
  return clientViewSdk.getBuyerProfile(slug);
}

export function patchBuyerProfile(slug, payload) {
  return clientViewSdk.patchBuyerProfile(slug, payload);
}

export async function uploadProfilePicture(slug, file) {
  const formData = new FormData();
  formData.append("image", file);

  const res = await fetch(
    `${getApiBaseUrl()}/api/v1/views/${encodeURIComponent(slug)}/me/profile-picture`,
    {
      method: "POST",
      credentials: "include",
      body: formData,
    }
  );

  const body = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw Object.assign(new Error(body.message || "Failed to upload profile picture"), {
      status: res.status,
    });
  }

  return body;
}

export async function getVendors(slug, params = {}) {
  const qs = new URLSearchParams();
  if (params.q) qs.set("q", params.q);
  if (params.vendorType) qs.set("vendorType", params.vendorType);
  if (params.page) qs.set("page", String(params.page));
  if (params.limit) qs.set("limit", String(params.limit));
  const suffix = qs.toString() ? `?${qs}` : "";
  const res = await fetch(`${getApiBaseUrl()}/api/v1/views/${slug}/vendors${suffix}`, {
    credentials: "include",
  });
  if (!res.ok) throw Object.assign(new Error("Failed to fetch vendors"), { status: res.status });
  return res.json();
}

/** Fetch all vendors for a view (paginates until complete). */
export async function getAllVendors(slug, params = {}) {
  const pageSize = params.limit || 100;
  let page = 1;
  let totalPages = 1;
  const vendors = [];

  while (page <= totalPages) {
    const body = await getVendors(slug, { ...params, page, limit: pageSize });
    vendors.push(...(body.vendors || []));
    totalPages = body.pagination?.totalPages || 1;
    page += 1;
  }

  return vendors;
}

export async function createVendor(slug, payload) {
  const res = await fetch(`${getApiBaseUrl()}/api/v1/views/${slug}/vendors`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw Object.assign(new Error("Failed to create vendor"), { status: res.status });
  return res.json();
}

export async function updateVendor(slug, vendorId, payload) {
  const res = await fetch(`${getApiBaseUrl()}/api/v1/views/${slug}/vendors/${vendorId}`, {
    method: "PUT",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw Object.assign(new Error("Failed to update vendor"), { status: res.status });
  return res.json();
}

/** Analytics — fail silently for UX. */
export async function trackEvent(slug, payload) {
  try {
    await clientViewSdk.trackEvent(slug, payload);
  } catch {
    // ignore
  }
}
